import { useState } from "react";
import { Checkbox, Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { Eye, Layers, Lock } from "lucide-react";
import { GearSix } from "@phosphor-icons/react";
import { AppButton, AppSwitch, FigmaTypography } from "@libs/zapi-ui/custom";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TSystemColumn } from "../lib/portfolioStorage";
import { SYSTEM_COLUMNS } from "../const/const";
import { ManageDimensionsModal } from "./ManageDimensionsModal/ManageDimensionsModal";
import { useTranslation } from "react-i18next";

type TProps = {
  dimensions: TOrganizationProjectDimension[];
  hiddenDimensionIds: string[];
  hiddenSystemColumns: TSystemColumn[];
  canManageDimensions: boolean;
  orgId: number;
  onToggleDimension: (id: string, checked: boolean) => void;
  onToggleSystemColumn: (col: TSystemColumn, checked: boolean) => void;
  iconOnly?: boolean;
  onHideAll: () => void;
  onShowAll: () => void;
};

export function PropertiesPopover(props: TProps) {
  const {
    dimensions,
    hiddenDimensionIds,
    hiddenSystemColumns,
    canManageDimensions,
    orgId,
    onToggleDimension,
    onToggleSystemColumn,
    iconOnly,
    onHideAll,
    onShowAll
  } = props;

  const { t } = useTranslation("projects");
  const [open, setOpen] = useState(false);
  const [manageDimOpen, setManageDimOpen] = useState(false);

  const hiddenDimSet = new Set(hiddenDimensionIds);
  const hiddenSysSet = new Set(hiddenSystemColumns);

  const systemColumns = iconOnly
    ? SYSTEM_COLUMNS.filter((c) => c.id !== "creator").map((c) =>
        c.id === "modified" ? { ...c, labelKey: "card.lastModified" as const } : c
      )
    : SYSTEM_COLUMNS;

  const hiddenCount =
    dimensions.filter((d) => hiddenDimSet.has(String(d.id))).length +
    systemColumns.filter((c) => hiddenSysSet.has(c.id)).length;
  const totalToggleable = dimensions.length + systemColumns.length;
  const allHidden = totalToggleable > 0 && hiddenCount === totalToggleable;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {iconOnly ? (
            <AppButton
              type="button"
              appearance="outlined"
              color="zinc150"
              aria-label={t("properties.heading")}
              className="h-8 w-8 max-w-8 shrink-0 rounded-lg !p-2"
            >
              <GearSix className="h-4 w-4 shrink-0 text-innoveraZinc-900" />
            </AppButton>
          ) : (
            <AppButton
              type="button"
              appearance="outlined"
              color="zinc150"
              className="h-8 shrink-0 gap-1 rounded-lg px-3 text-[14px] font-medium text-zinc-900 shadow-sm"
            >
              <Eye className="h-4 w-4 shrink-0 text-zinc-600" strokeWidth={1.5} />
              Properties
              {hiddenCount > 0 && (
                <FigmaTypography component="span" variant="label-3" className="ml-0.5 flex items-center justify-center rounded-[4px] bg-[#18181b] px-[5px] py-[3px] text-white">
                  {hiddenCount}
                </FigmaTypography>
              )}
            </AppButton>
          )}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="flex max-h-[var(--radix-popover-content-available-height)] w-[218px] flex-col overflow-y-auto rounded-lg border border-zinc-100 bg-white p-0 shadow-md"
          sideOffset={6}
          collisionPadding={8}
        >
          <div className="flex items-center justify-between border-b border-zinc-100 px-2 py-1">
            <span className="px-2 py-1 text-[14px] font-medium text-zinc-900">{t("properties.heading")}</span>
            <AppButton
              appearance="text"
              size="sm"
              color="zinc950"
              onClick={allHidden ? onShowAll : onHideAll}
            >
              {allHidden ? t("properties.showAll") : t("properties.hideAll")}
            </AppButton>
          </div>

          <div className="border-b border-zinc-100 px-2 py-1">
            <p className="px-2 pb-0.5 pt-1.5 text-[14px] leading-[22px] text-zinc-500">{t("properties.system")}</p>
            {iconOnly ? (
              <div className="flex cursor-not-allowed items-center justify-between gap-2 rounded-lg px-2 py-2 text-[14px] text-zinc-400">
                <span className="flex items-center gap-1.5">
                  {t("card.projectName")}
                  <Lock className="h-3.5 w-3.5 shrink-0" aria-label={t("properties.alwaysVisible")} />
                </span>
                <AppSwitch size="sm" checked disabled />
              </div>
            ) : (
              <label className="flex cursor-not-allowed items-center gap-1.5 rounded-lg px-2 py-2 text-[14px] text-zinc-400">
                <Checkbox
                  checked
                  disabled
                  className="rounded-[4px] border-transparent bg-[#a1a1aa] text-white disabled:opacity-100 data-[state=checked]:border-transparent data-[state=checked]:bg-[#a1a1aa] [&_svg]:h-3 [&_svg]:w-3"
                />
                Project
              </label>
            )}
            {systemColumns.map((col) =>
              iconOnly ? (
                <div key={col.id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-[14px] leading-[22px] text-zinc-900 hover:bg-zinc-50">
                  <span className="min-w-0 truncate">{t(col.labelKey)}</span>
                  <AppSwitch
                    size="sm"
                    checked={!hiddenSysSet.has(col.id)}
                    onCheckedChange={(v) => onToggleSystemColumn(col.id, v === true)}
                  />
                </div>
              ) : (
                <label key={col.id} className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-zinc-50 text-[14px] text-zinc-700">
                  <Checkbox
                    checked={!hiddenSysSet.has(col.id)}
                    onCheckedChange={(v) => onToggleSystemColumn(col.id, v === true)}
                    className="rounded-[4px] border border-[#e4e4e7] bg-transparent data-[state=checked]:border-transparent data-[state=checked]:bg-[#18181b] [&_svg]:h-3 [&_svg]:w-3"
                  />
                  {t(col.labelKey)}
                </label>
              )
            )}
          </div>

          <div className="px-2 py-1">
            <p className="px-2 pb-0.5 pt-1.5 text-[14px] leading-[22px] text-zinc-500">
              {t("properties.dimensions")}
            </p>
            {dimensions.length === 0 ? (
              <p className="px-2 py-2 text-[14px] text-zinc-400">{t("properties.noDimensions")}</p>
            ) : (
              dimensions.map((d) => (
                iconOnly ? (
                  <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-[14px] leading-[22px] text-zinc-900 hover:bg-zinc-50">
                    <span className="min-w-0 flex-1 truncate" title={d.name}>{d.name}</span>
                    <AppSwitch
                      size="sm"
                      checked={!hiddenDimSet.has(String(d.id))}
                      onCheckedChange={(v) => onToggleDimension(String(d.id), v === true)}
                    />
                  </div>
                ) : (
                <label key={d.id} className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-zinc-50 text-[14px] text-zinc-700">
                  <Checkbox
                    checked={!hiddenDimSet.has(String(d.id))}
                    onCheckedChange={(v) => onToggleDimension(String(d.id), v === true)}
                    className="rounded-[4px] border border-[#e4e4e7] bg-transparent data-[state=checked]:border-transparent data-[state=checked]:bg-[#18181b] [&_svg]:h-3 [&_svg]:w-3"
                  />
                  <span className="min-w-0 flex-1 truncate" title={d.name}>{d.name}</span>
                </label>
                )
              ))
            )}
            {canManageDimensions && (
              <div className="mt-1 border-t border-zinc-100 pb-1 pt-2">
                <AppButton
                  appearance="contained"
                  color="zinc100"
                  className="w-full"
                  onClick={() => {
                    setOpen(false);
                    setManageDimOpen(true);
                  }}
                >
                  <Layers className="h-4 w-4" />
                  {t("properties.manageDimensions")}
                </AppButton>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {canManageDimensions && (
        <ManageDimensionsModal
          open={manageDimOpen}
          onClose={() => setManageDimOpen(false)}
          orgId={orgId}
          dimensions={dimensions}
        />
      )}
    </>
  );
}
